import React, { Children, FC, PropsWithChildren, createContext, useContext, useMemo, useState } from 'react';
import { useObservable, useObserver } from '../../../../utils/useObservable';
import { usePropsForwarderContext } from '../../plugins/impl/context/ForwarderContext';
import { QueryModel, SearchModel, SortModel } from './DataTableQuery.types';
import { useStorageProcessor } from './initialQueryVariableProcessors/useStorageProcessor';
import { useQuickReportsProcessor } from './initialQueryVariableProcessors/useQuickReportProcessor';
import { useFilterProcessor } from './initialQueryVariableProcessors/useFilterProcessor';
import { useProcessorExecutor } from './initialQueryVariableProcessors/useProcessorExecutor';
import { useInitialSearchParamsProcessor } from './initialQueryVariableProcessors/useInitialSearchParamsProcessor';

const InitialSearchModelContext = createContext<{
  initialSearchModel: SearchModel<QueryModel, SortModel> | null;
  setFilterIds: (ids: string[]) => void;
}>({
  initialSearchModel: null,
  setFilterIds: () => {}
});

interface Props {
  plugins: React.ReactNode;
  dataTableId: string;
  setSortBy: (sort: { id: string; desc: boolean }[]) => void;
}

export const DataTableInitialSearchModelProvider: FC<PropsWithChildren<Props>> = ({
  children,
  dataTableId,
  setSortBy,
  plugins
}) => {
  const { getProps } = usePropsForwarderContext();
  const hasQuickReportExportPanel = useMemo(
    () =>
      !!Children.toArray(plugins).find(
        // We have to set if component is defined in the first render.
        // @ts-ignore: types are not correctly defined
        (it) => it?.type?.id === 'QuickReportsExportPanel'
      ),
    [plugins]
  );

  const { quickReports, loadingQuickReportsDone } = getProps('quickReports') ?? {};
  const [filterIds, setFilterIds] = useState<string[] | null>(null);
  const observable = useObservable<SearchModel<any, any>>(`dataTable:searchModel:init:${dataTableId}`);
  const storageProcessor = useStorageProcessor();
  const initialSearchParamsProcessor = useInitialSearchParamsProcessor();
  const loadingQR = hasQuickReportExportPanel ? (loadingQuickReportsDone ?? false) : undefined;

  const quickReportProcessor = useQuickReportsProcessor(quickReports, loadingQR);
  const filterProcessor = useFilterProcessor(filterIds);
  const [initialSearchModel, setInitialSearchModel] = useState<SearchModel<any, any> | null>(null);

  useProcessorExecutor({
    processors: [storageProcessor, initialSearchParamsProcessor, quickReportProcessor, filterProcessor],
    onChange: (processedValue) => {
      const nextValue = {
        query: processedValue?.query ?? { search: undefined },
        sort: processedValue?.sort,
        quickReportId: processedValue?.quickReportId
      };
      const targetSort = nextValue.sort?.[0];
      if (targetSort && targetSort.type) {
        setSortBy([{ id: targetSort.type, desc: targetSort.reversed }]);
      }

      observable.notify(nextValue);
      setInitialSearchModel(nextValue);
    }
  });

  const contextValue = useMemo(() => ({ initialSearchModel, setFilterIds }), [initialSearchModel]);

  return <InitialSearchModelContext.Provider value={contextValue}>{children}</InitialSearchModelContext.Provider>;
};

export function useDataTableInitialSearchModel() {
  return useContext(InitialSearchModelContext);
}

export function useDataTableInitialSearchModelObserver(
  dataTableId: string,
  fn: (data: CustomEvent<SearchModel<any, any>>) => void
) {
  useObserver(`dataTable:searchModel:init:${dataTableId}`, fn);
}
